/**
 * Selector cascada Región → Provincia → Distrito (INEI Perú).
 * Habilita/deshabilita selects según cobertura.
 */
import { useQuery } from "@tanstack/react-query";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { media, type Coverage } from "@/lib/api";

export type GeoValue = {
  region_id: number | null;
  province_id: number | null;
  district_id: number | null;
};

export interface PeruGeoPickerProps {
  coverage: Coverage;
  value: GeoValue;
  onChange: (v: GeoValue) => void;
  disabled?: boolean;
}

export function PeruGeoPicker({ coverage, value, onChange, disabled }: PeruGeoPickerProps) {
  const regionsQ = useQuery({ queryKey: ["media", "geo", "regions"], queryFn: () => media.listRegions(), staleTime: Infinity });
  const provincesQ = useQuery({
    queryKey: ["media", "geo", "provinces", value.region_id],
    queryFn: () => media.listProvinces(value.region_id ?? undefined),
    enabled: !!value.region_id,
    staleTime: Infinity,
  });
  const districtsQ = useQuery({
    queryKey: ["media", "geo", "districts", value.province_id],
    queryFn: () => media.listDistricts(value.province_id ?? undefined),
    enabled: !!value.province_id,
    staleTime: Infinity,
  });

  // Reglas según cobertura
  const regionEnabled = coverage !== "nacional";
  const provinceEnabled = (coverage === "local" || coverage === "zona") && !!value.region_id;
  const districtEnabled = (coverage === "local" || coverage === "zona") && !!value.province_id;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <Label className="text-xs">Región {regionEnabled && "*"}</Label>
        <Select
          disabled={disabled || !regionEnabled}
          value={value.region_id ? String(value.region_id) : ""}
          onValueChange={(v) => onChange({ region_id: Number(v), province_id: null, district_id: null })}
        >
          <SelectTrigger><SelectValue placeholder="Seleccionar región" /></SelectTrigger>
          <SelectContent>
            {(regionsQ.data ?? []).map(r => (
              <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Provincia {provinceEnabled && "*"}</Label>
        <Select
          disabled={disabled || !provinceEnabled}
          value={value.province_id ? String(value.province_id) : ""}
          onValueChange={(v) => onChange({ ...value, province_id: Number(v), district_id: null })}
        >
          <SelectTrigger><SelectValue placeholder="Seleccionar provincia" /></SelectTrigger>
          <SelectContent>
            {(provincesQ.data ?? []).map(p => (
              <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Distrito (opcional)</Label>
        <Select
          disabled={disabled || !districtEnabled}
          value={value.district_id ? String(value.district_id) : ""}
          onValueChange={(v) => onChange({ ...value, district_id: Number(v) })}
        >
          <SelectTrigger><SelectValue placeholder="Seleccionar distrito" /></SelectTrigger>
          <SelectContent>
            {(districtsQ.data ?? []).map(d => (
              <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
